'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable } from '@/components/data-table/data-table';
import { useDataTable } from '@/hooks/use-data-table';
import { MainTableState } from '@/lib/utils/table-state-utils';
import type { TableConfig } from '@/types/data-table';
import { DEFAULT_SKELETON_ROWS } from '@/config/data-table';
import type { AuditEvent, AuditEventOptions } from '@/lib/api/audit';

import { getAuditTrailTableColumns } from './audit-trail-table-columns';
import { AuditTrailTableSkeleton } from './AuditTrailTableSkeleton';

interface AuditTrailTableProps {
  auditEvents: AuditEvent[];
  total: number;
  options: AuditEventOptions;
  isLoading: boolean;
  onTableStateChange: (state: MainTableState) => void;
  onRowClick: (event: AuditEvent) => void;
  onRefetch?: () => void;
  config?: TableConfig;
}

/**
 * AuditTrailTable component for displaying the account's audit event log.
 * No free-text search: the backend has no GIN index on the `changes`/`context`
 * JSON columns, so search is deliberately unsupported on this endpoint.
 */
export function AuditTrailTable({
  auditEvents,
  total,
  options,
  isLoading,
  onTableStateChange,
  onRowClick,
  onRefetch,
  config = {},
}: AuditTrailTableProps) {
  const { t, i18n } = useTranslation('settings');

  const columns = React.useMemo(
    () => getAuditTrailTableColumns({ t, options }),
    [t, options, i18n.language],
  );

  // `useDataTable` snapshots the column defs — and therefore the faceted filter
  // options — when the table is created. The first render happens before the
  // query resolves, so without remounting once the options land, every filter
  // dropdown stays permanently empty. Same guard as `ExternalRequestsTable`.
  const optionsCount = options.action.length + options.entityType.length + options.actor.length;
  const hasLoadedOptionsRef = React.useRef(false);
  if (!hasLoadedOptionsRef.current && optionsCount > 0) {
    hasLoadedOptionsRef.current = true;
  }
  const tableKey = `${hasLoadedOptionsRef.current ? 'loaded' : 'loading'}-${i18n.language}`;

  const { table } = useDataTable({
    data: auditEvents,
    columns,
    total,
    config,
    onTableStateChange,
    getRowId: (row: AuditEvent) => row.id,
  });

  return (
    <DataTable
      key={tableKey}
      table={table}
      isLoading={isLoading}
      skeletonRows={<AuditTrailTableSkeleton rows={config.skeletonRows || DEFAULT_SKELETON_ROWS} />}
      onRefresh={onRefetch}
      onRowClick={onRowClick}
    />
  );
}
